import Actions from 'flow/actions';
import { fetchVideo2Config } from 'flow/actions/video2-actions';
import { v4 as uuidv4 } from 'uuid';
import { fromJS, List } from 'immutable';
import { DisplayMode } from 'flow/utils/rtc-utils';
import { MOCK_PARTICIPANTS_ENABLED } from 'flow/utils/meeting-mock';
import { segmentTrack } from 'flow/actions/segment-actions';
import { EVENT_STRINGS } from 'flow/utils/segment-utils';
import { getContactDataForNumber } from 'flow/selectors/contact-selectors';
import {
    getProcessedLocalStream,
    getScreenShareStream,
    stopStreamAndCleanup,
    stopStream,
} from 'flow/utils/video-utils';
import {
    CAMERA,
    MICROPHONE,
    setOutputDeviceWithIdToElement,
} from 'flow/utils/media-utils';
import { startMeeting, stopMeeting } from 'flow/api-clients/meeting-supervisor';
import {
    setPreferredCallCanvasSize,
    updateLayoutColumns,
} from 'flow/actions/app-actions';
import {
    setPreferredVideoInputId,
    receivePreferredAudioOutputId,
    receivePreferredMicrophoneId,
} from 'flow/actions/headset-actions';
import {
    getPreferredAudioOutputId,
    savePreferredAudioOutputId,
    savePreferredMicrophoneId,
} from 'flow/utils/browser-utils';
import { selectHeadsetsPreferredVideoInput } from 'flow/selectors/headset-selectors';
import { navigateTo } from 'flow/actions/navigate-actions';
import { selectNameInApp } from 'flow/selectors/selectors';
import { getFocusedActiveCall } from 'flow/selectors/call-selectors';
import { hangupSession, call } from 'flow/actions/phone-actions';
import { ExtraHeaders, CallMethodType } from 'flow/utils/call-utils';
import { setSnackbar } from 'flow/actions/app-actions';
import {
    WaitingRoomStanding,
    INTERNATIONAL_CONFERENCE_NUMBER,
} from 'flow/utils/meeting-constants';
import {
    restartPrimaryReceiverPeer,
    sendMessage,
    setupPrimarySenderPeerAndRTCListener,
    setupSecondarySenderPeer,
    closeSecondaryMemberPeer,
} from 'flow/api-clients/video-signaling-client';
import {
    stopLobbyAudioStream,
    setLobbyPinAction,
    setLobbyMicDeviceInUseAction,
    setLobbyVideoDeviceInUseAction,
    setLobbyAudioDeviceInUseAction,
    setLobbyCameraErrorAction,
    setLobbyMeetingCodeAction,
    setLobbyVideoActiveAction,
    setLobbyAudioActiveAction,
} from 'flow/actions/meeting-lobby-actions';
import {
    selectVideoActivityList,
    selectVideoSenderPrimaryPeer,
    selectVideoSenderScreenShareActive,
    selectVideoSenderScreenStream,
    selectVideoSenderVideoActive,
    selectVideoSenderVideoStream,
    selectVideoSecondarySenderPeer,
    selectVideoSecondarySenderVideoStream,
    selectVideoMemberVideoActive,
    selectVideoMemberScreenShareActive,
    selectVideoBackgroundBlurAmount,
    selectVideoBackgroundEffectMode,
    selectVideoBackgroundImageUrl,
    selectOwnWaitingRoomStanding,
} from 'flow/selectors/video-selectors';
import {
    addMeetingLog,
    extractSenderNameUuid,
    getMeetingDataFromAPI,
    getPinCodeFromAPI,
    MeetingLogLevel,
    rotateMeetingJoiningData,
} from 'flow/utils/meeting-utils';
import { registerParticipantIdentity } from 'flow/api-clients/participant-identity-client';
import { fetchChatSession } from 'flow/actions/chat-actions';
import { MeetingLocation } from 'flow/utils/meeting-constants';
import {
    checkAudioAndVideoPermissions,
    checkHasDevicePermission,
    findDeviceFromId,
    getAudioDeviceFromLocalStorage,
    getAvailableDevicesLists,
    getDefaultDevices,
    getMicDeviceFromLocalStorage,
    findDeviceRemoved,
    hasRemovedCurrentMic,
    hasRemovedCurrentAudio,
    hasRemovedCurrentVideo,
    renegotiateAudio,
} from 'flow/utils/meeting-devices-utils';
import {
    setOwnWaitingRoomStandingAction,
    setOwnUserIsSpeakingAction,
    setWaitingParticipantsAction,
    setVideoMemberPeerSetupAttemptsAction,
    setVideoMembersEmptyAction,
    setVideoMemberTrackActiveStatusAction,
    setVideoSenderPrimaryPeerAction,
    setVideoSenderScreenShareActiveAction,
    setVideoSenderScreenStreamAction,
    setVideoSenderStreamAction,
    setVideoSenderVideoActiveStatusAction,
    setVideoSecondarySenderStreamAction,
    setVideoSenderSecondaryPeerAction,
    setVideoShowScreenShareInGridStatusAction,
} from 'flow/actions/video-actions';
import {
    selectActiveMeetingAudioReference,
    selectActiveMeetingCallSession,
    selectActiveMeetingCode,
    selectActiveMeetingPollingInterval,
    selectAudioOutputDeviceList,
    selectIsUserLoggedIn,
    selectLobbyAudioActive,
    selectLobbyHostGateClosed,
    selectLobbyMeetingCode,
    selectLobbyVideoActive,
    selectLobbyVideoDeviceInUse,
    selectMicDeviceList,
    selectSenderAudioActive,
    selectSenderAudioStream,
    selectSenderName,
    selectVideoDeviceList,
    selectVideoInUse,
    selectMeetingIsActive,
    selectVideoAllowed,
    selectLobbyMicDeviceInUse,
    selectLobbyAudioDeviceInUse,
    selectActiveMeetingScreenShareMemberName,
    selectActiveMeetingChatOpen,
    selectActiveMeetingChatSessionId,
    selectActiveMeetingJoinInfoActive,
    selectActiveMeetingHostControlsActive,
    selectActiveMeetingHasPersistentChatHistory,
    selectActiveMeetingMembers,
} from 'flow/selectors/meeting-selectors';

export const joinMeeting =
    (name, meetingCode, viewerMustAskToJoin = false) =>
    async (dispatch, getState) => {
        const state = getState();
        dispatch(endActiveMeetingPollInterval()); // Ensure no other polling interval is present

        if (!meetingCode) {
            meetingCode = selectLobbyMeetingCode(state);
        }
        dispatch(setSenderName(name));

        if (!meetingCode) {
            dispatch(
                addMeetingLog(
                    'MeetingActions#joinMeeting: Cant join room, no meetingCode',
                ),
                MeetingLogLevel.ERROR,
            );
            return;
        } else if (!name) {
            dispatch(
                addMeetingLog(
                    'MeetingActions#joinMeeting: Cant join room, no senderName',
                ),
                MeetingLogLevel.ERROR,
            );
            return;
        }

        // VID-198: the meeting waits for its host and none has arrived. The
        // lobby disables its button for this, but Enter in the name field and
        // auto-join links reach here without it, and home does not enforce the
        // gate itself — so it is held here, on the state the lobby just
        // refreshed. A guest is held like anyone else: their ask to join
        // (VID-76) could only be answered by someone this gate is also holding.
        //
        // Scoped to the lobby's own meeting. Lobby state is only cleared when a
        // meeting ends, so a gated lobby left without joining lingers in the
        // store, and the entry paths that never fetch lobby data (accepting an
        // incoming video call, the Video2 accept, conferenceToMeeting) must not
        // be judged on it.
        if (
            meetingCode === selectLobbyMeetingCode(state) &&
            selectLobbyHostGateClosed(state)
        ) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#joinMeeting: not joining ${meetingCode}, waiting for host`,
                ),
            );
            return;
        }

        const activeMeetingCode = selectActiveMeetingCode(state);
        if (activeMeetingCode) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#joinMeeting: joining new room ${meetingCode}, hanging up ${activeMeetingCode}`,
                ),
            );
            await dispatch(endMeeting());
        }

        dispatch(stopLobbyAudioStream());
        //Set the devices selected in the lobby
        const micDeviceToUse = selectLobbyMicDeviceInUse(state);
        const audioDeviceToUse = selectLobbyAudioDeviceInUse(state);
        const videoDeviceToUse = selectLobbyVideoDeviceInUse(state);

        dispatch(setMicInUseAction(micDeviceToUse));
        dispatch(setAudioInUseAction(audioDeviceToUse));
        dispatch(setVideoInUseAction(videoDeviceToUse));

        const senderName = selectSenderName(getState());
        dispatch(
            addMeetingLog(
                `MeetingActions#joinMeeting: Joining meeting with code ${meetingCode}, as senderName ${senderName}`,
            ),
        );

        dispatch(
            addMeetingLog(
                `MeetingActions#joinMeeting: setting up polling interval for meeting code ${meetingCode}`,
            ),
        );
        const pollingInterval = setInterval(() => {
            dispatch(handleActiveMeetingPoll(meetingCode));
        }, 10000);
        dispatch(setActiveMeetingPollingIntervalAction(pollingInterval));
        dispatch(setActiveMeetingMeetingCodeAction(meetingCode));

        // A fresh attempt starts with a clean slate — otherwise the outcome of
        // a previous one (say a denial we showed in the lobby) would still be
        // sitting there when this join begins. VID-76.
        //
        // Someone who pressed "Ask to join" starts that attempt already
        // waiting, rather than with no standing at all. They have asked; the
        // answer is a socket and three requests away, and until it lands a
        // clean slate would read as "in the meeting" — which is how a guest
        // ended up being shown the meeting for the second before being told to
        // wait. Signaling confirms it with awaitingApproval, roomJoined clears
        // it if they were not held after all, and a failed join clears it
        // through resetMeetingState.
        dispatch(
            setOwnWaitingRoomStandingAction(
                viewerMustAskToJoin ? WaitingRoomStanding.WAITING : null,
            ),
        );
        dispatch(setJoinExpectedToBeHeldAction(viewerMustAskToJoin));

        // Awaited, unlike the roster read this also feeds: signaling asks home
        // whether this join uuid belongs to a logged-in user to decide who has
        // to wait for admission (VID-76), and it asks during joinRoom below.
        // Racing that call would put a colleague in the waiting room. It never
        // rethrows (see registerMeetingParticipantIdentity), so awaiting can
        // only delay the join, never fail it.
        await dispatch(registerMeetingParticipantIdentity(meetingCode));

        // Guests skip fetchAndStoreVideoConferenceConfig (auth-only endpoint),
        // so the videoChatUrl driving the side-channel WS must come from
        // fetchMeetingData's response (external endpoint includes it) before
        // startMeeting opens the socket. Internal users get the config from
        // both paths — harmless duplicate.
        await dispatch(fetchMeetingData(meetingCode));
        await dispatch(fetchAndStoreVideoConferenceConfig());

        // Start a call
        try {
            await dispatch(startMeeting());
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#joinMeeting: could not start meeting due to error: ${e}. Will hangup and inform user`,
                ),
            );
            dispatch(endMeeting());
            // Being turned away from the waiting room (VID-76) also lands here,
            // but it is not a failure to tell the user to reload from — the
            // lobby already explains what happened, and "try reloading" would
            // invite them to knock again in a loop.
            if (
                selectOwnWaitingRoomStanding(getState()) !==
                WaitingRoomStanding.DENIED
            ) {
                dispatch(
                    setSnackbar({
                        i18nextMessage: 'videoconference.error.reload',
                    }),
                );
            }
            throw new Error('Could not join meeting');
        }

        // Held when this join did not expect to be: home and signaling disagree
        // about whether this viewer is a colleague (see awaitingApproval, which
        // abandons the wait rather than sitting in it). The lookup behind that
        // disagreement is a distributed cache read that can race or time out, so
        // asking again usually gets the right answer. Once only, and the second
        // attempt goes in expecting to be held, so a hold that really is meant
        // is honoured rather than retried around.
        // Bounded by this attempt's own argument rather than by re-reading the
        // store: the retry passes true, so it cannot ask again whatever the
        // store says afterwards.
        if (
            selectOwnWaitingRoomStanding(getState()) ===
                WaitingRoomStanding.WAITING &&
            !viewerMustAskToJoin
        ) {
            dispatch(
                addMeetingLog(
                    'MeetingActions#joinMeeting: held without asking to be — rejoining once',
                    MeetingLogLevel.WARNING,
                ),
            );
            await dispatch(endMeeting());
            await dispatch(joinMeeting(name, meetingCode, true));
            return;
        }

        // Turned away: the join is over, so end it. Nothing else does — the
        // rejected joinRoom is swallowed inside startMeeting, which returns
        // normally, so without this the meeting stays "active" behind the
        // refusal: code still set, poll still running, and the lobby offering
        // its way back into a meeting we were just refused from. endMeeting
        // deliberately keeps the DENIED standing (see resetMeetingState), which
        // is what the lobby is showing. VID-76.
        if (
            selectOwnWaitingRoomStanding(getState()) ===
            WaitingRoomStanding.DENIED
        ) {
            await dispatch(endMeeting());
            return;
        }

        dispatch(segmentTrack(EVENT_STRINGS.USER_JOINED_ROOM));
        // Get data from API as there is new callers
        await dispatch(fetchMeetingData(meetingCode));
    };

const handleActiveMeetingPoll =
    (initialIntervalMeetingCode) => (dispatch, getState) => {
        // Dev-only: while mocking, useMockParticipants owns the member list.
        // Skip the poll so its fetched (real) members can't clobber the
        // injected mock set every cycle.
        if (MOCK_PARTICIPANTS_ENABLED) {
            return;
        }
        const state = getState();
        const activeMeetingCode = selectActiveMeetingCode(state);
        if (initialIntervalMeetingCode !== activeMeetingCode) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#handleActiveMeetingPoll: meeting code does not match: ${activeMeetingCode} - ${initialIntervalMeetingCode} - do not fetch meeting data`,
                ),
            );
            return;
        }
        dispatch(fetchMeetingData(initialIntervalMeetingCode));
    };

// For meeting entry paths that bypass the lobby UI (Video2 accept,
// conferenceToMeeting). lobby.videoActive is the source of truth that
// setupInitialTrackStatus reads, so we reset it here in case it was left true
// by a previous lobby session the user navigated away from.
export const joinMeetingWithCameraOff =
    (name, meetingCode) => async (dispatch) => {
        dispatch(setLobbyVideoActiveAction(false));
        dispatch(setVideoSenderVideoActiveStatusAction(false));
        await dispatch(joinMeeting(name, meetingCode));
    };

export const joinMeetingMuted = (name, meetingCode) => async (dispatch) => {
    await dispatch(setDevicesInfo());
    dispatch(setLobbyVideoActiveAction(false));
    dispatch(setVideoSenderVideoActiveStatusAction(false));
    dispatch(setLobbyAudioActiveAction(false));
    dispatch(setSenderAudioActiveStatusAction(false));
    await dispatch(joinMeeting(name, meetingCode));
};

export const endMeeting = () => async (dispatch) => {
    dispatch(addMeetingLog('MeetingActions#endMeeting: ending the meeting'));
    dispatch(endActiveMeetingPollInterval());
    try {
        await dispatch(stopMeeting());
        await dispatch(stopSenderVideoStream());
        await dispatch(stopSenderScreenStream());
        await dispatch(stopSenderSecondaryStream());
    } finally {
        // Even when a step above throws, the microphone is released and the
        // meeting does not linger in state.
        await dispatch(stopSenderAudioStream());
        dispatch(addMeetingLog('MeetingActions#endMeeting: Resetting state'));
        dispatch(resetMeetingState());
    }
};

const endActiveMeetingPollInterval = () => (dispatch, getState) => {
    dispatch(
        addMeetingLog(
            'MeetingActions#endActiveMeetingPollInterval: ending active meeting poll interval',
        ),
    );
    const pollingInterval = selectActiveMeetingPollingInterval(getState());
    clearInterval(pollingInterval);
    dispatch(setActiveMeetingPollingIntervalAction(null));
};

const resetMeetingState = () => (dispatch, getState) => {
    dispatch(setActiveMeetingCallIdAction(null));
    dispatch(setActiveMeetingCallSessionAction(null));
    dispatch(setActiveMeetingCallSessionConnectionStateAction(null));
    dispatch(setActiveMeetingCallUserAgentAction(null));
    dispatch(setActiveMeetingMeetingCodeAction(null));
    dispatch(setActiveMeetingIsHoveringAction(true));
    dispatch(setActiveMeetingMemberListActiveStatusAction(false));
    dispatch(setActiveMeetingChatOpenStatusAction(false));
    dispatch(setActiveMeetingJoinInfoActiveStatusAction(false));
    dispatch(setActiveMeetingIsOnHoldAction(false));
    dispatch(setActiveMeetingMembers(null));
    dispatch(setConferenceMeetingCallMembers([]));
    dispatch(setActiveMeetingPinAction(null));
    dispatch(setActiveMeetingChatSessionIdAction(null));
    dispatch(setActiveMeetingViewerIsMeetingAdminAction(false));
    dispatch(setActiveMeetingMeetingAccessAction(null));
    dispatch(setActiveMeetingHostControlsActiveStatusAction(false));
    dispatch(setActiveMeetingChatHistoryActivatedAction(false));
    dispatch(setActiveMeetingMeetingTypeAction(null));
    dispatch(setActiveMeetingScreenShareMemberNameAction(null));
    dispatch(setMeetingErrorAction(false, null, false, false));
    dispatch(resetSavedLogs());

    dispatch(setSenderAudioActiveStatusAction(true));
    dispatch(setVideoSenderVideoActiveStatusAction(true));
    dispatch(setVideoSenderScreenShareActiveAction(false));
    dispatch(setLobbyVideoActiveAction(false));
    dispatch(setVideoShowScreenShareInGridStatusAction(true));
    // setVideoMembersEmptyAction also clears pinned feeds (pins ⊆ members).
    dispatch(setVideoMembersEmptyAction());
    // The waiting room belonged to the meeting we just left (VID-76). A denial
    // survives: it ends the join through this very path, and the lobby still
    // has to tell the user what happened. Waiting does not — nobody is waiting
    // on a meeting they have already left, and leaving it standing greets them
    // with "waiting for approval" the next time they open the lobby.
    if (
        selectOwnWaitingRoomStanding(getState()) === WaitingRoomStanding.WAITING
    ) {
        dispatch(setOwnWaitingRoomStandingAction(null));
    }
    dispatch(setWaitingParticipantsAction(List()));
};

export const setSenderName = (senderName) => (dispatch) => {
    const uuid = uuidv4();
    const name = `${uuid}-${senderName}`;
    dispatch(
        addMeetingLog(`MeetingActions#setSenderName: Setting name ${name}`),
    );
    dispatch(setSenderNameAction(name));
};

/**
 * Registers this participant's join UUID against their authenticated identity
 * (VID-264), so the roster can classify them as external/colleague and expose
 * viewerIsMeetingAdmin. Guests are unauthenticated and can't reach this
 * endpoint — they classify as external by absence, which is already correct
 * for them, so this is skipped entirely when not logged in. Best-effort: a
 * failure here must never block the join, so it's caught and logged rather
 * than rethrown — the participant simply falls back to reading as external
 * until the next successful (re)join.
 */
export const registerMeetingParticipantIdentity =
    (meetingCode) => async (dispatch, getState) => {
        const state = getState();
        if (!selectIsUserLoggedIn(state)) {
            return;
        }
        const uuid = extractSenderNameUuid(selectSenderName(state));
        if (!uuid) {
            return;
        }
        try {
            await registerParticipantIdentity(meetingCode, uuid);
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#registerMeetingParticipantIdentity: failed for meetingCode ${meetingCode}: ${e}`,
                    MeetingLogLevel.WARNING,
                ),
            );
        }
    };

export const setupSenderVideoStream =
    (device) => async (dispatch, getState) => {
        const senderName = selectSenderName(getState());
        const deviceId = device && device.deviceId;
        try {
            dispatch(
                addMeetingLog(
                    `MeetingActions#setupSenderVideoStream: getting video stream for deviceId ${deviceId}`,
                ),
            );

            const blurAmount = selectVideoBackgroundBlurAmount(getState());
            const effectMode = selectVideoBackgroundEffectMode(getState());
            const backgroundImageUrl =
                selectVideoBackgroundImageUrl(getState());

            const videoStream = await getProcessedLocalStream(deviceId, {
                blurAmount: blurAmount,
                mode: effectMode,
                backgroundImageUrl: backgroundImageUrl,
            });

            dispatch(setVideoSenderStreamAction(videoStream));
            dispatch(
                addMeetingLog(
                    `MeetingActions#setupSenderVideoStream: Setting up video sender peer for ${senderName}`,
                ),
            );
            await dispatch(setupPrimarySenderPeerAndRTCListener(senderName));
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#setupSenderVideoStream: caught error ${e.name}: ${e.message}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            dispatch(handleVideoStreamSetupError(e));
        }
    };

const setupSenderScreenStream =
    (screenStream) => async (dispatch, getState) => {
        const senderName = selectSenderName(getState());
        try {
            dispatch(
                addMeetingLog(
                    `MeetingActions#setupSenderScreenStream: Setting up screen sender peer for ${senderName}`,
                ),
            );
            await dispatch(
                setupPrimarySenderPeerAndRTCListener(senderName, screenStream),
            );
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#setupSenderScreenStream: caught error ${e.name}: ${e.message}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            dispatch(handleVideoStreamSetupError(e));
        }
    };

const CAMERA_ERROR_NAMES = new Set([
    // Camera in use by another process (common on Windows).
    'NotReadableError',
    // Selected device is unavailable (unplugged, busy, or no longer satisfies constraints).
    'OverconstrainedError',
    // Selected device no longer exists.
    'NotFoundError',
]);

export const handleVideoStreamSetupError = (error) => (dispatch) => {
    if (error && CAMERA_ERROR_NAMES.has(error.name)) {
        dispatch(setLobbyCameraErrorAction(true));
    } else {
        // TODO: #video Error handling
    }
};

const stopSenderVideoStream = () => async (dispatch, getState) => {
    const state = getState();
    const senderPeer = selectVideoSenderPrimaryPeer(state);
    if (senderPeer) {
        dispatch(
            addMeetingLog(
                'MeetingActions#stopSenderVideoStream: disposing sender peer',
            ),
        );
        senderPeer.dispose();
    } else {
        dispatch(
            addMeetingLog(
                'MeetingActions#stopSenderVideoStream: no sender peer, cant dispose',
            ),
        );
    }
    const senderVideoStream = selectVideoSenderVideoStream(state);
    dispatch(
        addMeetingLog(
            'MeetingActions#stopSenderVideoStream: stopping sender stream',
        ),
    );
    await stopStreamAndCleanup(senderVideoStream);
    dispatch(setVideoSenderStreamAction(null));
    dispatch(setVideoSenderPrimaryPeerAction(null));
};

const stopSenderScreenStream = () => async (dispatch, getState) => {
    const state = getState();
    const senderPrimaryPeer = selectVideoSenderPrimaryPeer(state);
    if (senderPrimaryPeer) {
        dispatch(
            addMeetingLog(
                'MeetingActions#stopSenderScreenStream: disposing sender screen primary peer',
            ),
        );
        senderPrimaryPeer.dispose();
    } else {
        dispatch(
            addMeetingLog(
                'MeetingActions#stopSenderScreenStream: no sender primary peer, cant dispose',
            ),
        );
    }
    const senderScreenStream = selectVideoSenderScreenStream(state);
    dispatch(
        addMeetingLog(
            'MeetingActions#stopSenderScreenStream: stopping screen stream',
        ),
    );
    await stopStream(senderScreenStream);
    dispatch(setVideoSenderScreenStreamAction(null));
    dispatch(setVideoSenderPrimaryPeerAction(null));
};

const stopSenderSecondaryStream = () => async (dispatch, getState) => {
    const state = getState();
    const secondarySenderPeer = selectVideoSecondarySenderPeer(state);
    if (secondarySenderPeer) {
        dispatch(
            addMeetingLog(
                'MeetingActions#stopSenderSecondaryStream: disposing secondary sender peer',
            ),
        );
        secondarySenderPeer.dispose();
    } else {
        dispatch(
            addMeetingLog(
                'MeetingActions#stopSenderSecondaryStream: no secondary sender peer, cant dispose',
            ),
        );
    }
    const secondarySenderStream = selectVideoSecondarySenderVideoStream(state);
    dispatch(
        addMeetingLog(
            'MeetingActions#stopSenderSecondaryStream: stopping screen stream',
        ),
    );
    await stopStream(secondarySenderStream);
    dispatch(setVideoSecondarySenderStreamAction(null));
    dispatch(setVideoSenderSecondaryPeerAction(null));
};

// JsSIP stops the microphone it opened for the call, but not one that
// renegotiateAudio swapped in when the mic changed mid-meeting. The sender
// audio stream wraps whichever track the call is sending, so stop it here.
const stopSenderAudioStream = () => async (dispatch, getState) => {
    const senderAudioStream = selectSenderAudioStream(getState());
    dispatch(
        addMeetingLog(
            'MeetingActions#stopSenderAudioStream: stopping sender audio stream',
        ),
    );
    await stopStream(senderAudioStream);
    dispatch(setSenderAudioStreamAction(null));
};

export const fetchMeetingData = (meetingCode) => async (dispatch, getState) => {
    try {
        const isLoggedIn = selectIsUserLoggedIn(getState());
        const meetingData = await getMeetingDataFromAPI(
            meetingCode,
            isLoggedIn,
        );
        dispatch(setActiveMeetingPinAction(meetingData.get('pin')));
        dispatch(setActiveMeetingMembers(meetingData.get('conferenceMembers')));
        dispatch(setActiveMeetingNumberAction(meetingData.get('number')));
        const chatSessionId = meetingData.get('chatSessionId');
        dispatch(setActiveMeetingChatSessionIdAction(chatSessionId));
        dispatch(
            setActiveMeetingViewerIsMeetingAdminAction(
                meetingData.get('viewerIsMeetingAdmin'),
            ),
        );
        dispatch(
            setActiveMeetingMeetingAccessAction(
                meetingData.get('meetingAccess'),
            ),
        );
        dispatch(
            setActiveMeetingMeetingTypeAction(meetingData.get('meetingType')),
        );

        // Eagerly loads owner/roomType so isHost doesn't depend on VideoChatSession mounting first (AC-1685).
        // Keeps re-fetching every poll tick until chat history is persistent, not just once (AC-1637) — a participant's chat socket won't otherwise surface a later room update.
        if (isLoggedIn && chatSessionId) {
            const chatSessionKey = `chatsession-${chatSessionId}`;
            const hasPersistentHistory =
                selectActiveMeetingHasPersistentChatHistory(getState());
            if (!hasPersistentHistory) {
                dispatch(fetchChatSession(chatSessionKey, 'contact')).catch(
                    () => {},
                );
            }
        }
        const videoConferenceConfig = meetingData.get('videoConferenceConfig');
        if (videoConferenceConfig) {
            dispatch(
                setVideoConferenceConfigAction(videoConferenceConfig.toJS()),
            );
        }
    } catch (e) {
        dispatch(
            addMeetingLog(
                `MeetingActions#fetchMeetingData: caught error ${e.name}: ${e.message}`,
                MeetingLogLevel.ERROR,
            ),
        );
    }
};

export const toggleSenderMeetingAudioActive = () => (dispatch, getState) => {
    const audioActive = selectSenderAudioActive(getState());
    if (audioActive) {
        dispatch(muteAudio());
    } else {
        dispatch(unmuteAudio());
    }
};

const delay = (time) => new Promise((resolve) => setTimeout(resolve, time));

export const toggleSenderVideoActive = () => async (dispatch, getState) => {
    const videoActive = selectVideoSenderVideoActive(getState());
    const screenShareActive = selectVideoSenderScreenShareActive(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#toggleSenderVideoActive: screenShareActive ${screenShareActive}`,
        ),
    );
    if (screenShareActive) {
        dispatch(setVideoSenderVideoActiveStatusAction(!videoActive));
        if (videoActive) {
            await dispatch(muteSecondaryVideo());
        } else {
            await dispatch(unmuteSecondaryVideo());
        }
    } else {
        dispatch(
            addMeetingLog(
                `MeetingActions#toggleSenderVideoActive: videoActive ${videoActive}`,
            ),
        );
        if (videoActive) {
            await dispatch(muteVideo());
        } else {
            await dispatch(unmuteVideo());
        }
    }
    await delay(2000);
};

export const toggleSenderScreenShare =
    (sourceId) => async (dispatch, getState) => {
        const screenShareActive =
            selectVideoSenderScreenShareActive(getState());
        dispatch(
            addMeetingLog(
                `MeetingActions#toggleSenderScreenShare: screenShareActive ${screenShareActive}`,
            ),
        );
        if (screenShareActive) {
            await dispatch(stopScreenShare());
        } else {
            await dispatch(startScreenShare(sourceId));
        }
    };

export const stopScreenShare = () => async (dispatch, getState) => {
    const senderName = selectSenderName(getState());
    await dispatch(stopSenderScreenStream());
    await dispatch(stopSenderSecondaryStream());
    // Clear both screen-share flags together, up front — before the awaited
    // camera re-init below. This collapses the Main area straight from the
    // screen-share stage to the feed grid in one step. Leaving
    // screenShareActive set until the end kept the (now stream-less) stage
    // mounted for the whole re-init, then snapped to the grid — a visible
    // reflow. setupSenderVideoStream doesn't read this flag, so it's safe here.
    dispatch(setVideoSenderScreenShareActiveAction(false));
    dispatch(setVideoShowScreenShareInGridStatusAction(true)); //Reset the screen share in grid
    const videoActive = selectVideoSenderVideoActive(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#stopScreenShare: videoActive ${videoActive}`,
        ),
    );
    if (videoActive) {
        const videoDeviceInUse = selectVideoInUse(getState());
        await dispatch(setupSenderVideoStream(videoDeviceInUse));
    }
    dispatch(
        addMeetingLog(
            'MeetingActions#stopScreenShare: restarting and sending trackDisabled for screenShareActive',
        ),
    );
    dispatch(sendMessage({ id: 'restartVideo', sender: senderName }));
    dispatch(sendMessage({ id: 'trackDisabled', kind: 'screenShareActive' }));
};

const startScreenShare = (screenId) => async (dispatch, getState) => {
    const screenStream = await getScreenShareStream(screenId);
    if (!screenStream) {
        dispatch(
            addMeetingLog(
                'MeetingActions#startScreenShare: no screenStream when trying to start screen share',
                MeetingLogLevel.WARNING,
            ),
        );
        return;
    }
    const hadActiveVideoStream = !!selectVideoSenderVideoStream(getState());
    dispatch(setVideoSenderScreenShareActiveAction(true));
    const senderName = selectSenderName(getState());
    await dispatch(stopSenderVideoStream());
    if (hadActiveVideoStream) {
        await dispatch(setupSenderSecondaryVideoStream());
    }
    await dispatch(setupSenderScreenStream(screenStream));
    dispatch(setVideoSenderScreenStreamAction(screenStream));
    dispatch(
        addMeetingLog(
            'MeetingActions#startScreenShare: restarting and sending trackEnabled for screenShareActive',
        ),
    );
    dispatch(sendMessage({ id: 'restartVideo', sender: senderName }));
    dispatch(sendMessage({ id: 'trackEnabled', kind: 'screenShareActive' }));
};

const setupSenderSecondaryVideoStream = () => async (dispatch, getState) => {
    const videoActive = selectVideoSenderVideoActive(getState());
    if (videoActive) {
        const videoDeviceInUse = selectVideoInUse(getState());
        const videoDeviceId = videoDeviceInUse && videoDeviceInUse.deviceId;
        const senderName = selectSenderName(getState());
        try {
            dispatch(
                addMeetingLog(
                    `MeetingActions#startSecondarySenderScreenStream: getting video stream for deviceId ${videoDeviceId}`,
                ),
            );

            const blurAmount = selectVideoBackgroundBlurAmount(getState());
            const effectMode = selectVideoBackgroundEffectMode(getState());
            const backgroundImageUrl =
                selectVideoBackgroundImageUrl(getState());

            const videoStream = await getProcessedLocalStream(videoDeviceId, {
                blurAmount: blurAmount,
                mode: effectMode,
                backgroundImageUrl: backgroundImageUrl,
            });

            dispatch(setVideoSecondarySenderStreamAction(videoStream));
            dispatch(
                addMeetingLog(
                    `MeetingActions#startSecondarySenderScreenStream: Setting up secondary video sender peer for ${senderName}`,
                ),
            );
            await dispatch(setupSecondarySenderPeer(senderName));
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#startSecondarySenderScreenStream: caught error ${e.name}: ${e.message}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            dispatch(handleVideoStreamSetupError(e));
        }
    } else {
        dispatch(
            addMeetingLog(
                'MeetingActions#startSecondarySenderScreenStream: Video was not active, will not set up a stream',
            ),
        );
    }
};

export const toggleTrackChange =
    (memberName, trackKind, active) => async (dispatch) => {
        dispatch(
            addMeetingLog(
                `MeetingActions#toggleTrackChange: trackKind: ${trackKind} for ${memberName}, active: ${active}`,
            ),
        );
        switch (trackKind) {
            case 'audio':
                dispatch(
                    setVideoMemberTrackActiveStatusAction(
                        memberName,
                        'audioActive',
                        active,
                    ),
                );
                break;
            case 'video':
                await dispatch(
                    checkAndCloseReceiverPeer(memberName, trackKind, active),
                );
                dispatch(
                    setVideoMemberTrackActiveStatusAction(
                        memberName,
                        'videoActive',
                        active,
                    ),
                );
                break;
            case 'screenShareActive':
                dispatch(setScreenSharer(memberName, active));
                await dispatch(
                    checkAndCloseReceiverPeer(memberName, trackKind, active),
                );
                dispatch(
                    setVideoMemberTrackActiveStatusAction(
                        memberName,
                        'screenShareActive',
                        active,
                    ),
                );
                break;
            case 'screen':
            case 'screenShare':
                break; // Deprecated, server should use screenShareActive instead
            default:
                dispatch(
                    addMeetingLog(
                        `MeetingActions#toggleTrackChange: Track change error - trying to change track type ${trackKind}`,
                        MeetingLogLevel.ERROR,
                    ),
                );
                break;
        }
    };

const checkAndCloseReceiverPeer =
    (memberName, trackKind, active) => async (dispatch, getState) => {
        const state = getState();
        dispatch(
            addMeetingLog(
                `MeetingActions#checkAndCloseReceiverPeer: checking track ${trackKind}, activeStatus: ${active}`,
            ),
        );
        const memberVideoActive = selectVideoMemberVideoActive(
            state,
            memberName,
        );
        const memberScreenShareActive = selectVideoMemberScreenShareActive(
            state,
            memberName,
        );
        if (!active) {
            const turnOffVideoWhileScreenShare =
                trackKind === 'video' && memberScreenShareActive;
            const turnOffScreenShareWhileVideoActive =
                trackKind === 'screenShareActive' && memberVideoActive;
            if (
                turnOffScreenShareWhileVideoActive ||
                turnOffVideoWhileScreenShare
            ) {
                await dispatch(closeSecondaryMemberPeer(memberName));
            }
        }
    };

const setScreenSharer = (memberName, active) => (dispatch, getState) => {
    const currentScreenSharer =
        selectActiveMeetingScreenShareMemberName(getState());
    if (active && !currentScreenSharer) {
        dispatch(setActiveMeetingScreenShareMemberNameAction(memberName));
    } else if (currentScreenSharer === memberName && !active) {
        dispatch(setActiveMeetingScreenShareMemberNameAction(null));
        dispatch(setVideoShowScreenShareInGridStatusAction(true));
    }
};

export const setDevicesInfo = () => async (dispatch, getState) => {
    await checkAudioAndVideoPermissions();
    const devicesLists = await getAvailableDevicesLists();
    const { micList, audioList, videoList } = devicesLists;
    dispatch(setMicDeviceListAction(micList));
    dispatch(setAudioDeviceListAction(audioList));
    dispatch(setVideoDeviceListAction(videoList));

    const micAllowed = await checkHasDevicePermission(MICROPHONE);
    const videoAllowed = await checkHasDevicePermission(CAMERA);
    dispatch(
        addMeetingLog(
            `MeetingActions#setDevicesInfo: setting mic and video permissions - micAllowed: ${micAllowed}, videoAllowed: ${videoAllowed}`,
        ),
    );
    dispatch(setMicAllowedAction(micAllowed));
    dispatch(setVideoAllowedAction(videoAllowed));

    const defaultDevices = await getDefaultDevices();
    const appVideoId = selectHeadsetsPreferredVideoInput(getState());
    let videoDeviceInUse = defaultDevices.defaultVideo;
    if (appVideoId) {
        const videoDevice = videoList.find(
            (device) => device.deviceId === appVideoId,
        );
        videoDeviceInUse = videoDevice ? videoDevice : videoDeviceInUse;
    }
    dispatch(setLobbyVideoDeviceInUseAction(videoDeviceInUse));

    let audioDevice = getAudioDeviceFromLocalStorage(getState());
    if (!audioDevice) {
        audioDevice = defaultDevices.defaultAudio;
    }
    dispatch(setLobbyAudioDeviceInUseAction(audioDevice));

    let micDevice = getMicDeviceFromLocalStorage(getState());
    if (!micDevice) {
        micDevice = defaultDevices.defaultMic;
    }
    dispatch(setLobbyMicDeviceInUseAction(micDevice));
};

export const updateDevices = () => async (dispatch, getState) => {
    const state = getState();

    const deviceDifferences = await findDeviceRemoved(state);
    const { micDiff, audioDiff, videoDiff } = deviceDifferences;

    const removedCurrentMic = hasRemovedCurrentMic(micDiff, state);
    const removedCurrentAudio = hasRemovedCurrentAudio(audioDiff, state);
    const removedCurrentVideo = hasRemovedCurrentVideo(videoDiff, state);

    if (removedCurrentMic || removedCurrentAudio || removedCurrentVideo) {
        dispatch(
            setSnackbar({
                i18nextMessage: 'videoconference.lost_current_device',
            }),
        );

        const meetingIsActive = selectMeetingIsActive(getState());
        const meetingLocation = meetingIsActive
            ? MeetingLocation.ACTIVE_MEETING
            : MeetingLocation.LOBBY;
        if (removedCurrentMic) {
            await dispatch(updateMicDevice(meetingLocation));
        }
        if (removedCurrentAudio) {
            await dispatch(updateAudioDevice(meetingLocation));
        }
        if (removedCurrentVideo) {
            await dispatch(updateVideoDevice(meetingLocation));
        }
    }

    const devicesLists = await getAvailableDevicesLists();
    const { micList, audioList, videoList } = devicesLists;
    dispatch(setMicDeviceListAction(micList));
    dispatch(setAudioDeviceListAction(audioList));
    dispatch(setVideoDeviceListAction(videoList));
};

const updateMicDevice = (meetingLocation) => async (dispatch) => {
    await dispatch(changeMicDevice('default', meetingLocation, false));
};

const updateAudioDevice = (meetingLocation) => async (dispatch) => {
    await dispatch(changeAudioDevice('default', meetingLocation, false));
};

const updateVideoDevice = (meetingLocation) => async (dispatch) => {
    await dispatch(changeVideoDevice('default', meetingLocation, false));
};

export const requestPin = (meetingCode) => async (dispatch, getState) => {
    const state = getState();
    const resultPin = await getPinCodeFromAPI(meetingCode);
    dispatch(
        addMeetingLog(
            `MeetingActions#requestPin: requesting PIN, result given: ${!!resultPin}`,
        ),
    );
    if (resultPin) {
        const activeMeetingMeetingCode = selectActiveMeetingCode(state);
        if (meetingCode === activeMeetingMeetingCode) {
            dispatch(setActiveMeetingPinAction(resultPin));
        } else {
            dispatch(setLobbyPinAction(resultPin));
        }
    }
    return resultPin;
};

export const rotateMeetingData =
    (meetingCode) => async (dispatch, getState) => {
        const [newMeetingCode, newPin] =
            await rotateMeetingJoiningData(meetingCode);
        dispatch(
            addMeetingLog(
                `MeetingActions#refreshMeetingData: rotating meeting data for ${meetingCode}. New meeting code: ${newMeetingCode}, new pin: ${newPin}`,
            ),
        );
        if (newMeetingCode && newPin) {
            const activeMeetingMeetingCode =
                selectActiveMeetingCode(getState());
            if (meetingCode === activeMeetingMeetingCode) {
                dispatch(setActiveMeetingMeetingCodeAction(newMeetingCode));
                dispatch(setActiveMeetingPinAction(newPin));
            } else {
                dispatch(setLobbyMeetingCodeAction(newMeetingCode));
                dispatch(setLobbyPinAction(newPin));
            }
        }
    };

export const muteAudio = () => (dispatch, getState) => {
    const callSession = selectActiveMeetingCallSession(getState());
    if (callSession) {
        dispatch(addMeetingLog('MeetingActions#muteAudio: muting audio'));
        callSession.mute({ audio: true });
        dispatch(sendMessage({ id: 'trackDisabled', kind: 'audio' }));
        dispatch(setSenderAudioActiveStatusAction(false));
    } else {
        dispatch(
            addMeetingLog(
                'MeetingActions#muteAudio: could not mute audio - no callSession',
                MeetingLogLevel.ERROR,
            ),
        );
        //todo Could not mute?
    }
};

const unmuteAudio = () => (dispatch, getState) => {
    const callSession = selectActiveMeetingCallSession(getState());
    if (callSession) {
        dispatch(addMeetingLog('MeetingActions#unmuteAudio: unmuting audio'));
        callSession.unmute({ audio: true });
        dispatch(sendMessage({ id: 'trackEnabled', kind: 'audio' }));
        dispatch(setSenderAudioActiveStatusAction(true));
    } else {
        dispatch(
            addMeetingLog(
                'MeetingActions#unmuteAudio: could not unmute audio - no callSession',
                MeetingLogLevel.ERROR,
            ),
        );
        //todo Could not unmute?
    }
};

const muteVideo = () => async (dispatch) => {
    dispatch(addMeetingLog('MeetingActions#muteVideo: muting video'));
    dispatch(setVideoSenderVideoActiveStatusAction(false));
    await dispatch(stopSenderVideoStream());
    dispatch(sendMessage({ id: 'trackDisabled', kind: 'video' }));
};

const unmuteVideo = () => async (dispatch, getState) => {
    dispatch(addMeetingLog('MeetingActions#unmuteVideo: unmuting video'));
    dispatch(setVideoSenderVideoActiveStatusAction(true));
    const videoDeviceInUse = selectVideoInUse(getState());
    await dispatch(setupSenderVideoStream(videoDeviceInUse));
    dispatch(sendMessage({ id: 'trackEnabled', kind: 'video' }));
    // setupSenderVideoStream tears down and rebuilds the sender's upstream
    // endpoint from scratch (VID-490) — trackEnabled alone only flips the
    // viewers' videoActive flag, it never rebuilds their receiver peer
    // (checkAndCloseReceiverPeer only acts on mute, or on screen-share).
    // Without this, every other participant's tile stays wired to the now-
    // disposed endpoint and appears permanently frozen. Mirrors the same
    // broadcast already sent after every other sender-peer rebuild (screen
    // share start/stop, device change, reconnect). Deliberately omitted on
    // the mute branch — see forceMuteSenderVideo's doc comment.
    const senderName = selectSenderName(getState());
    dispatch(sendMessage({ id: 'restartVideo', sender: senderName }));
};

const muteSecondaryVideo = () => async (dispatch) => {
    dispatch(
        addMeetingLog(
            'MeetingActions#muteVideo: muting secondary video and sending video trackDisabled',
        ),
    );
    dispatch(setVideoSenderVideoActiveStatusAction(false));
    await dispatch(stopSenderSecondaryStream());
    dispatch(sendMessage({ id: 'trackDisabled', kind: 'video' }));
};

const unmuteSecondaryVideo = () => async (dispatch) => {
    dispatch(
        addMeetingLog(
            'MeetingActions#unmuteVideo: unmuting secondary video and sending trackEnabled',
        ),
    );
    dispatch(setVideoSenderVideoActiveStatusAction(true));
    await dispatch(setupSenderSecondaryVideoStream());
    dispatch(sendMessage({ id: 'trackEnabled', kind: 'video' }));
};

/**
 * Directed "turn my camera off" — the one-way mirror of muteAudio(), used when a
 * remote participant asks us to turn our camera off (VID-53). It reuses the same
 * feed-specific mute as toggleSenderVideoActive's off-path (secondary while
 * screen-sharing, so a live share is left untouched; primary otherwise) — that
 * path never needs a restartVideo broadcast, only its unmute counterpart does
 * (VID-490) — but without the toggle's trailing 2s delay or its re-read of
 * videoActive that could flip to unmute. Structurally it can only ever turn
 * the camera off.
 */
export const forceMuteSenderVideo = () => async (dispatch, getState) => {
    const screenShareActive = selectVideoSenderScreenShareActive(getState());
    if (screenShareActive) {
        await dispatch(muteSecondaryVideo());
    } else {
        await dispatch(muteVideo());
    }
};

/**
 * Ask another participant to turn off their camera. The signaling server relays
 * this to the target participant, whose client disables its own video (see the
 * `turnOffVideoRequested` handler in video-signaling-client). The target can
 * turn their camera back on afterwards.
 *
 * @param {string} targetName - the signaling name of the participant whose video
 *   should be turned off (the same `name` used in track/peer messages).
 */
export const turnOffParticipantVideo = (targetName) => (dispatch, getState) => {
    if (!targetName) {
        return;
    }
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#turnOffParticipantVideo: ${senderName} requesting to turn off video for ${targetName}`,
        ),
    );
    dispatch(sendMessage({ id: 'turnOffVideoFor', name: targetName }));
};

/**
 * Ask another participant to mute their microphone. The signaling server relays
 * this to the target participant, whose client mutes its own audio (see the
 * `muteAudioRequested` handler in video-signaling-client). The target can unmute
 * again afterwards.
 *
 * @param {string} targetName - the signaling name of the participant to mute
 *   (the same `name` used in track/peer messages).
 */
export const muteParticipant = (targetName) => (dispatch, getState) => {
    if (!targetName) {
        return;
    }
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#muteParticipant: ${senderName} requesting to mute ${targetName}`,
        ),
    );
    dispatch(sendMessage({ id: 'muteAudioFor', name: targetName }));
};

/**
 * Ask every other participant to turn off their camera (VID-53 AC2). The
 * signaling server relays the request to all participants except the requester
 * and the active screen-sharer; each target's client turns off its own camera.
 * Targets can turn their cameras back on afterwards.
 */
export const turnOffAllParticipantsVideo = () => (dispatch, getState) => {
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#turnOffAllParticipantsVideo: ${senderName} requesting to turn off all cameras`,
        ),
    );
    dispatch(sendMessage({ id: 'turnOffVideoForAll' }));
};

/**
 * Ask every other participant to mute their microphone (VID-53 AC2). The
 * signaling server relays the request to all participants except the requester
 * and the active screen-sharer; each target's client mutes itself. Targets can
 * unmute again afterwards.
 */
export const muteAllParticipants = () => (dispatch, getState) => {
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#muteAllParticipants: ${senderName} requesting to mute all`,
        ),
    );
    dispatch(sendMessage({ id: 'muteAudioForAll' }));
};

/**
 * Let a waiting participant into the meeting (VID-76). Any internal participant
 * may do this — there is no host role (see VID-24's permission matrix) — and the
 * server verifies the sender is internal before acting.
 *
 * Idempotent by design: the server claims the awaiting entry on the first
 * verdict and ignores anything addressed to a name it no longer holds, so a
 * double-click, or two people acting at once, settles on one outcome.
 *
 * Message ids are `admit` / `deny` / `admitAll` / `denyAll`, as signaling
 * handles them in GroupCallWebSocket. They break the `<verb>For` pattern of the
 * neighbouring host controls (muteAudioFor, removeParticipantFor); that is the
 * server's spelling, not a slip on this side.
 *
 * @param {string} name - their join name, `<uuid>-<nick>`.
 */
export const admitWaitingParticipant = (name) => (dispatch, getState) => {
    if (!name) {
        return;
    }
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#admitWaitingParticipant: ${senderName} allowing ${name}`,
        ),
    );
    dispatch(sendMessage({ id: 'admit', name }));
};

/**
 * Turn a waiting participant away (VID-76). Same permission model, same
 * idempotency and the same note on the message id as admitWaitingParticipant.
 *
 * @param {string} name - their join name, `<uuid>-<nick>`.
 */
export const denyWaitingParticipant = (name) => (dispatch, getState) => {
    if (!name) {
        return;
    }
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#denyWaitingParticipant: ${senderName} denying ${name}`,
        ),
    );
    dispatch(sendMessage({ id: 'deny', name }));
};

/**
 * Give up on waiting to be let in (VID-76) and drop back to the lobby proper.
 * Ending the meeting closes the signaling socket, and that is what releases our
 * place in the waiting room — there is no "withdraw" message. It also clears
 * our own admission standing (see resetMeetingState), so the lobby comes back
 * as it was rather than still claiming we are waiting.
 */
export const cancelWaitingForAdmission = () => async (dispatch) => {
    dispatch(
        addMeetingLog(
            'MeetingActions#cancelWaitingForAdmission: giving up on the waiting room',
        ),
    );
    await dispatch(endMeeting());
};

/**
 * Let everyone currently waiting in (VID-76). One message rather than a message
 * per person, so the room settles in a single step and nobody is half-admitted
 * if the sender drops midway. Same permission model and message-id note as
 * admitWaitingParticipant.
 */
export const admitAllWaitingParticipants = () => (dispatch, getState) => {
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#admitAllWaitingParticipants: ${senderName} allowing everyone waiting`,
        ),
    );
    dispatch(sendMessage({ id: 'admitAll' }));
};

/** Turn everyone currently waiting away (VID-76). See admitAllWaitingParticipants. */
export const denyAllWaitingParticipants = () => (dispatch, getState) => {
    const senderName = selectSenderName(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#denyAllWaitingParticipants: ${senderName} denying everyone waiting`,
        ),
    );
    dispatch(sendMessage({ id: 'denyAll' }));
};

// Remove another participant from the meeting (VID-311/VID-57): tears down their video leg via signaling and their audio leg via home's ConferencesResource#removeConferenceMember.
export const removeParticipant = (targetName) => async (dispatch, getState) => {
    if (!targetName) {
        return;
    }
    const state = getState();
    const senderName = selectSenderName(state);
    dispatch(
        addMeetingLog(
            `MeetingActions#removeParticipant: ${senderName} requesting to remove ${targetName}`,
        ),
    );
    dispatch(sendMessage({ id: 'removeParticipantFor', name: targetName }));

    const meetingCode = selectActiveMeetingCode(state);
    const targetUuid = extractSenderNameUuid(targetName);
    if (!meetingCode || !targetUuid) {
        dispatch(
            addMeetingLog(
                `MeetingActions#removeParticipant: missing meetingCode or targetUuid for ${targetName}, audio leg not hung up`,
                MeetingLogLevel.WARNING,
            ),
        );
        return;
    }
    try {
        await new Promise((resolve, reject) => {
            ConferencesResource.removeConferenceMember(
                { onResult: resolve, onError: reject, onClientError: reject },
                meetingCode,
                targetUuid,
            );
        });
    } catch (e) {
        dispatch(
            addMeetingLog(
                `MeetingActions#removeParticipant: removeConferenceMember failed for ${targetName}: ${e}`,
                MeetingLogLevel.WARNING,
            ),
        );
    }
};

export const changeMicDevice =
    (newMicDeviceId, meetingLocation, saveToLocalStorage = true) =>
    async (dispatch, getState) => {
        const micList = selectMicDeviceList(getState());
        const micDevice = findDeviceFromId(micList, newMicDeviceId);
        if (meetingLocation === MeetingLocation.ACTIVE_MEETING) {
            dispatch(
                addMeetingLog(
                    'MeetingActions#changeMicDevice: change mic in the active meeting with SIP renegotiation',
                ),
            );
            const swapped = await dispatch(
                renegotiateAudio(micDevice, micList),
            );
            if (swapped) {
                await dispatch(setSenderAudioStream());
            }
            dispatch(setMicInUseAction(micDevice));
            dispatch(setLobbyMicDeviceInUseAction(micDevice));
        } else {
            dispatch(
                addMeetingLog(
                    'MeetingActions#changeMicDevice: change mic, no active meeting',
                ),
            );
            dispatch(setLobbyMicDeviceInUseAction(micDevice));
        }
        if (saveToLocalStorage) {
            savePreferredMicrophoneId(newMicDeviceId);
            dispatch(receivePreferredMicrophoneId(newMicDeviceId));
        }
    };

export const setSenderAudioStream = () => async (dispatch, getState) => {
    const oldSenderAudioStream = selectSenderAudioStream(getState());
    if (oldSenderAudioStream) {
        dispatch(
            addMeetingLog(
                'MeetingActions#setSenderAudioStream: old sender audio stream found, stopping it',
            ),
        );
        await stopStream(oldSenderAudioStream);
    }
    const callSession = selectActiveMeetingCallSession(getState());
    if (callSession && callSession.connection) {
        const sender = callSession.connection.getSenders()[0];
        const audioTrack = sender && sender.track;
        if (audioTrack) {
            dispatch(
                addMeetingLog(
                    'MeetingActions#setSenderAudioStream: setting sender audio stream',
                ),
            );
            const mediaStream = new MediaStream();
            mediaStream.addTrack(audioTrack);
            dispatch(setSenderAudioStreamAction(mediaStream));
        } else {
            dispatch(
                addMeetingLog(
                    'MeetingActions#setSenderAudioStream: no audioTrack found',
                    MeetingLogLevel.WARNING,
                ),
            );
        }
    } else {
        dispatch(
            addMeetingLog(
                'MeetingActions#setSenderAudioStream: no callSession or callSession.connection found',
                MeetingLogLevel.ERROR,
            ),
        );
    }
};

export const changeAudioDevice =
    (newAudioDeviceId, meetingLocation, saveToLocalStorage = true) =>
    (dispatch, getState) => {
        const deviceList = selectAudioOutputDeviceList(getState());
        const audioDevice = findDeviceFromId(deviceList, newAudioDeviceId);
        dispatch(
            addMeetingLog(
                `MeetingActions#changeAudioDevice: meetingLocation ${meetingLocation}`,
            ),
        );
        if (meetingLocation === MeetingLocation.ACTIVE_MEETING) {
            const audioReference =
                selectActiveMeetingAudioReference(getState());
            dispatch(
                addMeetingLog(
                    `MeetingActions#changeAudioDevice: updating audio reference to ${newAudioDeviceId}`,
                ),
            );
            setOutputDeviceWithIdToElement(newAudioDeviceId, audioReference);
            dispatch(setAudioInUseAction(audioDevice));
            dispatch(setLobbyAudioDeviceInUseAction(audioDevice));
        } else {
            dispatch(setLobbyAudioDeviceInUseAction(audioDevice));
        }

        if (saveToLocalStorage) {
            savePreferredAudioOutputId(newAudioDeviceId);
            dispatch(receivePreferredAudioOutputId(newAudioDeviceId));
        }
    };

export const linkAudioDeviceIdToAudioReference = () => (dispatch, getState) => {
    const audioID = getPreferredAudioOutputId();
    if (audioID) {
        dispatch(
            addMeetingLog(
                `MeetingActions#setUpAudioDeviceIdToElementLink: updating audio reference to ${audioID}`,
            ),
        );
        const audioReference = selectActiveMeetingAudioReference(getState());
        setOutputDeviceWithIdToElement(audioID, audioReference);
    }
};

export const changeVideoDevice =
    (newVideoDeviceId, meetingLocation, saveToLocalStorage = true) =>
    async (dispatch, getState) => {
        const state = getState();
        const senderName = selectSenderName(state);
        const videoList = selectVideoDeviceList(state);
        const videoDevice = findDeviceFromId(videoList, newVideoDeviceId);
        dispatch(
            addMeetingLog(
                `MeetingActions#changeVideoDevice: meetingLocation ${meetingLocation}`,
            ),
        );
        if (meetingLocation === MeetingLocation.ACTIVE_MEETING) {
            dispatch(
                addMeetingLog(
                    'MeetingActions#changeVideoDevice: changing active meeting video',
                ),
            );
            const videoActive = selectVideoSenderVideoActive(state);
            dispatch(setVideoInUseAction(videoDevice));
            if (videoActive) {
                const screenShareActive =
                    selectVideoSenderScreenShareActive(state);
                if (screenShareActive) {
                    dispatch(
                        addMeetingLog(
                            'MeetingActions#changeVideoDevice: screen share, restarting secondary video',
                        ),
                    );
                    await dispatch(stopSenderSecondaryStream());
                    await dispatch(setupSenderSecondaryVideoStream());
                } else {
                    dispatch(
                        addMeetingLog(
                            'MeetingActions#changeVideoDevice: no screen share, restarting video',
                        ),
                    );
                    await dispatch(stopSenderVideoStream());
                    await dispatch(setupSenderVideoStream(videoDevice));
                    dispatch(
                        sendMessage({ id: 'restartVideo', sender: senderName }),
                    );
                }
            }
        } else {
            dispatch(
                addMeetingLog(
                    'MeetingActions#changeVideoDevice: changing lobby video',
                ),
            );
            dispatch(setLobbyVideoDeviceInUseAction(videoDevice));
        }
        if (saveToLocalStorage) {
            dispatch(setPreferredVideoInputId(newVideoDeviceId));
        }
    };

export const toggleMeetingChatActive = () => (dispatch, getState) => {
    const chatIsOpen = selectActiveMeetingChatOpen(getState());
    dispatch(setActiveMeetingChatOpenStatusAction(!chatIsOpen));
    if (!chatIsOpen) {
        dispatch(setActiveMeetingMemberListActiveStatusAction(false));
        dispatch(setActiveMeetingJoinInfoActiveStatusAction(false));
        dispatch(setActiveMeetingHostControlsActiveStatusAction(false));
    }
};

export const openAssociatedChatSession = () => (dispatch, getState) => {
    const chatSessionId = selectActiveMeetingChatSessionId(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#openAssociatedChatSession: opening chatsession ${chatSessionId}`,
        ),
    );
    if (chatSessionId) {
        dispatch(navigateTo(`/messages/chatsession-${chatSessionId}`));
    }
};

export const setActiveMeetingMemberListActiveStatus =
    (memberListStatus) => (dispatch) => {
        dispatch(
            setActiveMeetingMemberListActiveStatusAction(memberListStatus),
        );
        if (memberListStatus) {
            dispatch(setActiveMeetingChatOpenStatusAction(false));
            dispatch(setActiveMeetingJoinInfoActiveStatusAction(false));
            dispatch(setActiveMeetingHostControlsActiveStatusAction(false));
        }
    };

export const toggleMeetingJoinInfoActive = () => (dispatch, getState) => {
    const joinInfoIsActive = selectActiveMeetingJoinInfoActive(getState());
    dispatch(setActiveMeetingJoinInfoActiveStatusAction(!joinInfoIsActive));
    if (!joinInfoIsActive) {
        dispatch(setActiveMeetingChatOpenStatusAction(false));
        dispatch(setActiveMeetingMemberListActiveStatusAction(false));
        dispatch(setActiveMeetingHostControlsActiveStatusAction(false));
    }
};

export const closeMeetingJoinInfo = () => (dispatch) => {
    dispatch(setActiveMeetingJoinInfoActiveStatusAction(false));
};

export const openMeetingJoinInfo = () => (dispatch) => {
    dispatch(setActiveMeetingJoinInfoActiveStatusAction(true));
    dispatch(setActiveMeetingChatOpenStatusAction(false));
    dispatch(setActiveMeetingMemberListActiveStatusAction(false));
    dispatch(setActiveMeetingHostControlsActiveStatusAction(false));
};

export const toggleMeetingHostControlsActive = () => (dispatch, getState) => {
    const hostControlsIsActive =
        selectActiveMeetingHostControlsActive(getState());
    dispatch(
        setActiveMeetingHostControlsActiveStatusAction(!hostControlsIsActive),
    );
    if (!hostControlsIsActive) {
        dispatch(setActiveMeetingChatOpenStatusAction(false));
        dispatch(setActiveMeetingMemberListActiveStatusAction(false));
        dispatch(setActiveMeetingJoinInfoActiveStatusAction(false));
    }
};

export const closeMeetingHostControls = () => (dispatch) => {
    dispatch(setActiveMeetingHostControlsActiveStatusAction(false));
};

export const setViewToFullscreen = () => (dispatch) => {
    dispatch(
        addMeetingLog(
            'MeetingActions#setViewToFullscreen: setting view to fullscreen',
        ),
    );
    dispatch(setActiveMeetingViewAction(DisplayMode.FULLSCREEN.key));
    dispatch(setActiveMeetingMemberListActiveStatusAction(false));
    dispatch(setActiveMeetingChatOpenStatusAction(false));
    dispatch(setActiveMeetingJoinInfoActiveStatusAction(false));
};

export const setViewToSideBar = () => (dispatch) => {
    const meetingView = DisplayMode.THUMBNAIL.key;
    dispatch(
        addMeetingLog(
            'MeetingActions#setViewToSideBar: setting view to sidebar',
        ),
    );
    dispatch(setActiveMeetingViewAction(meetingView));
    dispatch(setPreferredCallCanvasSize(meetingView));
    dispatch(updateLayoutColumns());
    dispatch(setActiveMeetingMemberListActiveStatusAction(false));
    dispatch(setActiveMeetingChatOpenStatusAction(false));
    dispatch(setActiveMeetingJoinInfoActiveStatusAction(false));
};

export const setViewToMinimized = () => (dispatch) => {
    dispatch(
        addMeetingLog(
            'MeetingActions#setViewToMinimized: setting view to minimized',
        ),
    );
    const meetingView = DisplayMode.MINIMIZED.key;

    dispatch(setActiveMeetingViewAction(meetingView));
    dispatch(setPreferredCallCanvasSize(meetingView));
    dispatch(updateLayoutColumns());
    dispatch(setActiveMeetingMemberListActiveStatusAction(false));
    dispatch(setActiveMeetingChatOpenStatusAction(false));
    dispatch(setActiveMeetingJoinInfoActiveStatusAction(false));
};

// lobby.videoActive is the source of truth for the camera-off-by-default
// behavior (VID-195). This must run before setupSenderVideoStream in
// startMeeting — muteVideo() here flips sender.videoActive, which gates the
// stream setup that follows.
export const setupInitialTrackStatus = () => async (dispatch, getState) => {
    const state = getState();
    dispatch(
        addMeetingLog(
            'MeetingActions#setupInitialTrackStatus: Sending initial track status',
        ),
    );
    const audioActive = selectLobbyAudioActive(state);
    if (!audioActive) {
        dispatch(
            addMeetingLog(
                'MeetingActions#setupInitialTrackStatus: audioActive set to false, muting audio',
            ),
        );
        dispatch(muteAudio());
    }
    const videoActive = selectLobbyVideoActive(state);
    const videoAllowed = selectVideoAllowed(state);
    if (!videoActive || !videoAllowed) {
        dispatch(
            addMeetingLog(
                'MeetingActions#setupInitialTrackStatus: videoActive set to false, muting video',
            ),
        );
        await dispatch(muteVideo());
    }
};

export const putMeetingOnHold = (shouldHold) => (dispatch, getState) => {
    const hasActiveMeeting = selectMeetingIsActive(getState());
    dispatch(
        addMeetingLog(
            `MeetingActions#putMeetingOnHold: hasActiveMeeting ${hasActiveMeeting}`,
        ),
    );
    if (hasActiveMeeting) {
        if (shouldHold) {
            dispatch(
                addMeetingLog(
                    'MeetingActions#putMeetingOnHold: Setting meeting on hold',
                ),
            );
            dispatch(muteVideo());
            dispatch(muteAudio());
        }
        dispatch(setActiveMeetingIsOnHoldAction(shouldHold));
    }
};

export const sendUserActive = () => (dispatch, getState) => {
    const activityList = selectVideoActivityList(getState());
    const ownName = selectSenderName(getState());
    const meetingIsActive = selectMeetingIsActive(getState());
    if (activityList.get(0) !== ownName && meetingIsActive) {
        dispatch(sendMessage({ id: 'userActive' }));
    }
};

// Report the own user's speaking state. Drives the own tile's highlight locally
// (instant, no round-trip) via ownUserIsSpeaking, and tells the backend (VID-223)
// — which keeps a per-room speaking set and relays a `userSpeakingChanged
// { speakers }` snapshot for *other* participants. That set renders the remote
// speaking highlight (VID-222) and, since VID-219, also drives this client's
// active-speaker tile ordering. Sent on the start/stop edges and re-asserted on
// a keepalive while speaking (see useOwnSpeakingDetection). Like `userActive`,
// the backend identifies the member from the websocket connection, so no sender
// is sent. The legacy `userActive`/`activityList` heartbeat is now kept only for
// legacy clients that still order their grid off it, pending removal once they
// age out.
export const broadcastOwnSpeaking = (isSpeaking) => (dispatch) => {
    dispatch(setOwnUserIsSpeakingAction(isSpeaking));
    dispatch(sendMessage({ id: 'userSpeaking', speaking: isSpeaking }));
};

export const removeDuplicateMembers = (list) => {
    const sortedList = list.sortBy((x) => x.get('joinTime'));
    const groupedList = sortedList.groupBy((x) => x.get('displayName'));
    return groupedList.map((x) => x.first()).toList();
};

export const setActiveMeetingMembers = (members) => (dispatch, getState) => {
    const nextMembers = members ? removeDuplicateMembers(members) : fromJS([]);
    // The meeting-members poll (fetchMeetingData) fires this every tick. Skip the
    // dispatch when the membership is value-equal to what's already stored so the
    // activeMeetingMembers reference is preserved on idle polls — otherwise every
    // poll swaps the reference and re-renders every meetingMembers consumer
    // (MeetingVideoGrid, useVideoSubscriptions, …). Real changes (join/leave)
    // are not equal and still propagate.
    const currentMembers = selectActiveMeetingMembers(getState());
    if (currentMembers && currentMembers.equals(nextMembers)) {
        return;
    }
    dispatch(setActiveMeetingMembersAction(nextMembers));
};

export const restartReceiverPeerResetAttemptCount =
    (memberName) => (dispatch) => {
        dispatch(setVideoMemberPeerSetupAttemptsAction(memberName, 0));
        dispatch(restartPrimaryReceiverPeer(memberName));
    };

export const resetSavedLogs = () => (dispatch) => {
    dispatch(setLogsWhenNoNetworkAction([]));
    dispatch(setNumberOfMissedLogsAction(0));
};

export const getContactFromNumber = (number) => (_dispatch, getState) => {
    return getContactDataForNumber(getState(), number);
};

export const conferenceToMeeting = () => async (dispatch, getState) => {
    const state = getState();
    const focusedActiveCall = getFocusedActiveCall(state);
    const meetingCode =
        focusedActiveCall &&
        focusedActiveCall.getIn(['conference', 'meetingCode']);

    await dispatch(setDevicesInfo());
    dispatch(setViewToSideBar());
    dispatch(hangupSession());

    const name = selectNameInApp(state);

    try {
        await dispatch(joinMeetingWithCameraOff(name, meetingCode));
    } catch (e) {
        dispatch(
            addMeetingLog(
                `Failed to join meeting with video from active conference, calling back into conference. ${e}`,
            ),
        );
        dispatch(
            setSnackbar({ i18nextMessage: 'videoconference.error.reconnect' }),
        );
        const extraCallOptions = {
            extraHeaders: [
                `${ExtraHeaders.CONFERENCE_MEETING_CODE}: ${meetingCode}`,
            ],
        };
        await dispatch(
            call(
                INTERNATIONAL_CONFERENCE_NUMBER,
                CallMethodType.LOCAL,
                extraCallOptions,
            ),
        );
    }
};

export const recieveMeetingMemberCount = (topMessage) => (dispatch) => {
    const { MeetingCode, MemberCount } = topMessage;
    const memberCountConvert = parseInt(MemberCount); //Top sends a string, we want a number
    dispatch(setMeetingMemberCountAction(MeetingCode, memberCountConvert));
};
import ConferencesResource from 'flow/api/conferences';

export const fetchAllMeetingNumbers = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        ConferencesResource.getConferenceNumbers({
            onResult(json) {
                dispatch(setMeetingNumbersAction(json));
                resolve();
            },
            onError: (/*error*/) => {
                reject();
            },
        });
    });
};

// ### Actions

const setMeetingMemberCountAction = (meetingCode, memberCount) => ({
    type: Actions.MEETING_SET_MEETING_MEMBER_COUNT,
    meetingCode,
    memberCount,
});

const setActiveMeetingMeetingCodeAction = (meetingCode) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_MEETING_CODE,
    meetingCode,
});

const setJoinExpectedToBeHeldAction = (joinExpectedToBeHeld) => ({
    type: Actions.MEETING_SET_JOIN_EXPECTED_TO_BE_HELD,
    joinExpectedToBeHeld,
});

export const setSenderNameAction = (senderName) => ({
    type: Actions.MEETING_SET_SENDER_NAME,
    senderName,
});

export const setActiveMeetingCallIdAction = (callId) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_CALL_ID,
    callId,
});

export const setActiveMeetingAudioReferenceAction = (audioReference) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_AUDIO_REFERENCE,
    audioReference,
});

export const clearMeetingStateAction = () => ({
    type: Actions.MEETING_CLEAR_STATE,
});

export const clearMeetingMemberCountsAction = () => ({
    type: Actions.MEETING_CLEAR_MEMBER_COUNTS,
});

export const setMeetingNumbersAction = (numbersList) => ({
    type: Actions.MEETING_SET_MEETING_NUMBERS,
    numbersList,
});

export const setActiveMeetingCallSessionAction = (callSession) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_CALL_SESSION,
    callSession,
});

export const setActiveMeetingCallSessionConnectionStateAction = (
    connectionState,
) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_CALL_CONNECTION_STATE,
    connectionState,
});

export const setActiveMeetingViewAction = (meetingView) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_VIEW,
    meetingView,
});

export const setActiveMeetingCallUserAgentAction = (userAgent) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_CALL_USER_AGENT,
    userAgent,
});

export const setActiveMeetingPinAction = (pin) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_PIN,
    pin,
});

export const setActiveMeetingMembersAction = (conferenceMembers) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_MEMBERS,
    conferenceMembers,
});

export const setActiveMeetingNumberAction = (number) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_NUMBER,
    number,
});

export const setActiveMeetingChatSessionIdAction = (chatSessionId) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_CHAT_SESSION_ID,
    chatSessionId,
});

export const setActiveMeetingViewerIsMeetingAdminAction = (
    viewerIsMeetingAdmin,
) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_VIEWER_IS_MEETING_ADMIN,
    viewerIsMeetingAdmin,
});

export const setActiveMeetingMeetingAccessAction = (meetingAccess) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_MEETING_ACCESS,
    meetingAccess,
});

export const setActiveMeetingChatHistoryActivatedAction = (
    chatHistoryActivated,
) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_CHAT_HISTORY_ACTIVATED,
    chatHistoryActivated,
});

export const setActiveMeetingMeetingTypeAction = (meetingType) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_MEETING_TYPE,
    meetingType,
});

export const linkChatSessionToMeeting =
    (meetingCode, chatSessionKey, { onSuccess, onError } = {}) =>
    (dispatch) => {
        ConferencesResource.updateChatConference(
            {
                onResult() {
                    const rawId = chatSessionKey.replace('chatsession-', '');
                    dispatch(setActiveMeetingChatSessionIdAction(rawId));
                    dispatch(setActiveMeetingChatHistoryActivatedAction(true));
                    onSuccess?.();
                },
                onError() {
                    dispatch(setSnackbar({ i18nextMessage: 'snackbar.error' }));
                    onError?.();
                },
            },
            meetingCode,
            fromJS({ key: chatSessionKey }),
        );
    };

export const setActiveMeetingIsOnHoldAction = (isOnHold) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_IS_ON_HOLD,
    isOnHold,
});

export const setActiveMeetingPollingIntervalAction = (pollingInterval) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_POLLING_INTERVAL,
    pollingInterval,
});

export const setActiveMeetingIsHoveringAction = (isHovering) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_IS_HOVERING,
    isHovering,
});

export const setActiveMeetingIsNarrowWindowAction = (isNarrowWindow) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_IS_NARROW_WINDOW,
    isNarrowWindow,
});

const setActiveMeetingMemberListActiveStatusAction = (membersListActive) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_MEMBER_LIST_ACTIVE,
    membersListActive,
});

const setActiveMeetingJoinInfoActiveStatusAction = (joinInfoActive) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_JOIN_INFO_ACTIVE,
    joinInfoActive,
});

const setActiveMeetingHostControlsActiveStatusAction = (
    hostControlsActive,
) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_HOST_CONTROLS_ACTIVE,
    hostControlsActive,
});

export const setActiveMeetingMonitorAction = (monitorInterval) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_MONITOR_INTERVAL,
    monitorInterval,
});

export const setActiveMeetingReconnectAttemptAction = (reconnectAttempt) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_RECONNECT_ATTEMPT,
    reconnectAttempt,
});

export const setActiveMeetingReconnectActiveAction = (reconnectActive) => ({
    type: Actions.MEETING_SET_ACTIVE_MEETING_RECONNECT_ACTIVE,
    reconnectActive,
});

export const setActiveMeetingScreenShareMemberNameAction = (
    screenShareMemberName,
) => ({
    type: Actions.MEETING_SET_ACTIVE_SCREEN_SHARER,
    screenShareMemberName,
});

export const setActiveMeetingChatOpenStatusAction = (chatOpen) => ({
    type: Actions.MEETING_SET_CHAT_OPEN_STATUS,
    chatOpen,
});

export const setSenderAudioActiveStatusAction = (audioActive) => ({
    type: Actions.MEETING_SET_SENDER_AUDIO_ACTIVE_STATUS,
    audioActive,
});

export const setSenderAudioStreamAction = (audioStream) => ({
    type: Actions.MEETING_SET_SENDER_AUDIO_STREAM,
    audioStream,
});

export const setMicAllowedAction = (micAllowed) => ({
    type: Actions.MEETING_SET_MIC_ALLOWED,
    micAllowed,
});

export const setVideoAllowedAction = (videoAllowed) => ({
    type: Actions.MEETING_SET_VIDEO_ALLOWED,
    videoAllowed,
});

export const setMicInUseAction = (deviceInUse) => ({
    type: Actions.MEETING_SET_MIC_DEVICE_IN_USE,
    deviceInUse,
});

const setAudioInUseAction = (deviceInUse) => ({
    type: Actions.MEETING_SET_AUDIO_DEVICE_IN_USE,
    deviceInUse,
});

const setVideoInUseAction = (deviceInUse) => ({
    type: Actions.MEETING_SET_VIDEO_DEVICE_IN_USE,
    deviceInUse,
});

export const setMicDeviceListAction = (deviceList) => ({
    type: Actions.MEETING_SET_MIC_DEVICE_LIST,
    deviceList,
});

export const setAudioDeviceListAction = (deviceList) => ({
    type: Actions.MEETING_SET_AUDIO_DEVICE_LIST,
    deviceList,
});

export const setVideoDeviceListAction = (deviceList) => ({
    type: Actions.MEETING_SET_VIDEO_DEVICE_LIST,
    deviceList,
});

export const setMeetingErrorAction = (
    show,
    text,
    spinner,
    reconnectOnClick,
) => ({
    type: Actions.MEETING_SET_ERROR,
    show,
    text,
    spinner,
    reconnectOnClick,
});

export const setNumberOfMissedLogsAction = (numberOfMissedLogs) => ({
    type: Actions.MEETING_SET_NUMBER_OF_MISSED_LOGS,
    numberOfMissedLogs,
});

export const setLogsWhenNoNetworkAction = (logs) => ({
    type: Actions.MEETING_SET_NETWORK_ERROR_LOGS,
    logs,
});

export const setConferenceMeetingCallMembers = (callingMembers) => ({
    type: Actions.MEETING_SET_CALLING_PARTICIPANTS,
    callingMembers,
});

export const setVideoConferenceConfigAction = (videoConferenceConfig) => ({
    type: Actions.MEETING_SET_VIDEO_CONFERENCE_CONFIG,
    videoConferenceConfig,
});

export const fetchAndStoreVideoConferenceConfig =
    () => async (dispatch, getState) => {
        if (!selectIsUserLoggedIn(getState())) {
            return;
        }
        try {
            const config = await fetchVideo2Config();
            dispatch(setVideoConferenceConfigAction(config));
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#fetchAndStoreVideoConferenceConfig: failed to fetch config: ${e}`,
                    MeetingLogLevel.ERROR,
                ),
            );
        }
    };
